/**
 * Table row-selections snapshotted per workspace tab.
 *
 * A workspace-tab switch is a plain router navigation, so a list page's
 * selection would either die with the page tree (different route) or be reset
 * by the scope-key change (same route, other params). Parking the ids here —
 * outside React, keyed by tab id — lets `useTableRowSelection` restore them
 * when the tab becomes active again. Deliberately not persisted: row ids go
 * stale across reloads, unlike the tabs themselves.
 */
interface TabSelectionSnapshot {
  /** The view (list + page + range) the ids belong to — restored only when it
   *  matches, so ids from another page or filter set never leak in. */
  scopeKey: string
  ids: string[]
}

/** Snapshot slot used before the tab store adopts a first tab. */
const NO_TAB = ''

const snapshots = new Map<string, TabSelectionSnapshot>()

export function readTabSelection(
  tabId: string | null,
  scopeKey: string,
): string[] {
  const snapshot = snapshots.get(tabId ?? NO_TAB)
  if (snapshot?.scopeKey !== scopeKey) return []
  return snapshot.ids
}

export function writeTabSelection(
  tabId: string | null,
  scopeKey: string,
  ids: string[],
): void {
  snapshots.set(tabId ?? NO_TAB, { scopeKey, ids })
}

/** A closed workspace tab's selection dies with it. */
export function dropTabSelection(tabId: string | null): void {
  snapshots.delete(tabId ?? NO_TAB)
}
